import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { Style, StatusBar } from '@capacitor/status-bar';
import { useEffect } from 'react';

import { useTheme } from '../shared/web/theme/theme-context';

import { appSession } from './session';

/**
 * The two pieces of platform chrome the shell owns, wired to the app's state.
 *
 * Renders nothing. It lives in `app/` because it is composition — connecting
 * the platform to what the application already knows — and not in
 * `shared/web/shell/`, which is HOM-8's chrome and is what the app draws
 * itself.
 *
 * Every call is guarded by `isNativePlatform`. `StatusBar` has no web
 * implementation and rejects when called there, and an unhandled rejection from
 * cosmetic chrome would be reported as a real error by the tracker mounted one
 * file over.
 */
export function NativeChrome() {
  const { resolved } = useTheme();

  /*
   * The status bar has to follow the theme, or one of the two ships with the
   * platform drawing its clock and battery in the colour chosen for the other.
   * Verified on a device rather than reasoned about: the first version of this
   * had the mapping inverted, and it looked entirely plausible in code.
   *
   * **`Style.Dark` means LIGHT TEXT, for a dark background.** The plugin names
   * the style after the background it is for, which is the opposite of how
   * every other theme value in this codebase reads. That is the whole reason
   * this comment exists.
   *
   * The background colour is Android-only — iOS derives it from the view — and
   * it is read out of the resolved theme rather than written here, because
   * colour lives in exactly one file. Reading the computed property means
   * HOM-21's values reach the system bar with nothing to update.
   */
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    void StatusBar.setStyle({ style: resolved === 'dark' ? Style.Dark : Style.Light });

    if (Capacitor.getPlatform() !== 'android') return;

    /*
     * Read after the frame, not in this effect body.
     *
     * `ThemeProvider` stamps `data-theme` onto the document in ITS effect, and
     * React runs a child's effects BEFORE its parent's — so reading the
     * computed value here gets the theme we just left. Caught on a device:
     * switching to light sent the dark canvas, and the style and the colour
     * disagreed with each other while both looked correct in isolation.
     *
     * The style above is safe in the effect body because it comes from
     * `resolved`, a value. Only the colour has to come from the DOM, because
     * colour lives in exactly one file and that file is a stylesheet.
     */
    const frame = requestAnimationFrame(() => {
      const canvas = getComputedStyle(document.documentElement).getPropertyValue('--canvas').trim();
      // A no-op on Android 15+, which removed the API — the app paints that
      // area itself there. Still correct for everything down to our minSdk.
      if (canvas !== '') void StatusBar.setBackgroundColor({ color: canvas });
    });
    return () => cancelAnimationFrame(frame);
  }, [resolved]);

  /*
   * Dismissed by the application, once there is something to show. Never on a
   * timer: `capacitor.config.ts` turns the automatic hide off, because a timed
   * splash either covers a ready app or uncovers an unready one, and which of
   * those you get depends on the device.
   *
   * This runs after the first render of the tree beneath `ThemeProvider`, by
   * which point the pre-paint bootstrap in `root.tsx` has already stamped the
   * resolved theme onto the document — so what the splash uncovers is the
   * user's theme, not a flash of the other one. That flash is the entire reason
   * the bootstrap is inline, and hiding the splash too early would reintroduce
   * it on the one platform it was written for.
   */
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    void SplashScreen.hide();
  }, []);

  /*
   * The session's refresh follows the app's foreground state (HOM-61).
   *
   * The library refreshes the access token on a ticker, and on the web it
   * pauses that ticker while the page is hidden and resumes it on
   * `visibilitychange`. A webview does not reliably deliver that event, so
   * Supabase's guidance for a native shell is to drive the ticker from the
   * platform's own foreground and background events — which is what this
   * is. On web the library handles it itself and this effect does nothing.
   */
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handle = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) appSession().startAutoRefresh();
      else appSession().stopAutoRefresh();
    });

    return () => {
      void handle.then((listener) => listener.remove());
    };
  }, []);

  /*
   * The Android hardware back button.
   *
   * HOM-17's `sheet-history.ts` dismisses a sheet on `popstate`, and pushes a
   * history entry per open sheet precisely so that Back means "close this"
   * rather than "leave". That was written for this moment.
   *
   * It does not work on its own, which was worth finding out on a device: the
   * Capacitor Android bridge has **no back-button handling of its own at all**,
   * so without this listener Android's default runs and the activity finishes —
   * pressing Back with a wizard open drops the user onto the home screen. The
   * plugin documentation's note that listening "disables the default behaviour"
   * describes the plugin's default, not the platform's.
   *
   * So: go back while the webview has anywhere to go, which lets `popstate`
   * reach the sheet layer, and only leave when it genuinely does not.
   * `exitApp` is deliberate rather than implicit — on Android, backing out of
   * the first screen is expected to close the app, and swallowing it would be
   * the opposite bug.
   */
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return;

    const handle = App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else void App.exitApp();
    });

    return () => {
      void handle.then((listener) => listener.remove());
    };
  }, []);

  return null;
}
